import type { ReactNode } from "react";
import { Alert, Center, Loader, Stack, Text, ThemeIcon } from "@mantine/core";
import { IconAlertTriangle, IconDatabaseOff, IconInbox } from "@tabler/icons-react";
import { isPermissionError } from "@/lib/errors";

export function LoadingState({ label = "Loading…" }: { label?: string }) {
  return (
    <Center py="xl">
      <Stack align="center" gap="xs">
        <Loader size="sm" />
        <Text c="dimmed" size="sm">
          {label}
        </Text>
      </Stack>
    </Center>
  );
}

export function ErrorState({ error }: { error: Error }) {
  const isPermission = isPermissionError(error);
  return (
    <Alert
      color="red"
      variant="light"
      icon={<IconAlertTriangle size={18} />}
      title={isPermission ? "Access denied" : "Couldn't load"}
    >
      {isPermission
        ? "You don't have access to this data. It requires admin access — if you were just " +
          "granted it, sign out and back in."
        : error.message}
    </Alert>
  );
}

export function EmptyState({
  title = "Nothing here",
  description,
  icon,
}: {
  title?: string;
  description?: ReactNode;
  icon?: ReactNode;
}) {
  return (
    <Center py="xl">
      <Stack align="center" gap="xs" maw={420} ta="center">
        <ThemeIcon variant="light" color="gray" size="xl" radius="xl">
          {icon ?? <IconInbox size={22} />}
        </ThemeIcon>
        <Text fw={600}>{title}</Text>
        {description ? (
          <Text c="dimmed" size="sm">
            {description}
          </Text>
        ) : null}
      </Stack>
    </Center>
  );
}

/** Shown for data sets that aren't available to the console yet. */
export function NotProjectedState() {
  return (
    <EmptyState
      icon={<IconDatabaseOff size={22} />}
      title="Not available yet"
      description="This data isn't available to the console yet. It'll appear here automatically once it's ready — no action needed."
    />
  );
}
